import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { UserQueryDto } from '../../api/models/user-query-dto';
import { updateUser } from '../../users/state/actions/user.actions';
import { selectUserProfile } from '../state/selector';
import { trimWhitespaceValidator } from '../../shared/validators/trim-whitespace.validator';
import { humanNameValidator } from '../../shared/validators/human-name.validator';
import { TenantConfig, TenantService } from '../service/tenantService';

@Component({
  selector: 'app-edit-user-profile',
  templateUrl: './edit-user-profile.component.html',
  styleUrl: './edit-user-profile.component.css',
  imports: [Dialog, ReactiveFormsModule, InputText, Button],
})
export class EditUserProfileComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);

  userProfile!: UserQueryDto;

  /** SSO users' names are managed by their identity provider — never prompt them to confirm. */
  isSsoUser = false;

  profileForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.maxLength(100),
      trimWhitespaceValidator(),
      humanNameValidator(),
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.maxLength(100),
      trimWhitespaceValidator(),
      humanNameValidator(),
    ]),
  });

  ngOnInit(): void {
    this.store.select(selectUserProfile).subscribe((userProfile) => {
      this.userProfile = userProfile;
      if (!this.profileForm.get('firstName')?.value) {
        this.profileForm.patchValue({
          firstName: this.userProfile.firstName,
          lastName: this.userProfile.lastName,
        });
      }

      this.tenantService.config$.subscribe((cfg) => {
        this.isSsoUser = this.isExternallyManaged(this.userProfile, cfg);
      });
    });
  }

  /** Mirrors the backend's SsoUtils.isExternallyManaged: only admin/standard users are blocked
   * from self-managing their name once externalUserManagementEnabled is on and their email
   * domain is SSO-linked. A plain isSsoEmail check (ssoEnabled + domain only) would wrongly
   * suppress this for suppliers, or for any user when externalUserManagementEnabled is off,
   * even though the backend still permits PATCH /users/me for them (#58537 review). */
  private isExternallyManaged(user: UserQueryDto, cfg: TenantConfig | null): boolean {
    if (!cfg?.ssoEnabled || !cfg.externalUserManagementEnabled || !cfg.ssoDomains?.length || !user.email) {
      return false;
    }
    if (user.accessControl !== 'admin' && user.accessControl !== 'standard') return false;
    const domain = user.email.split('@').pop()?.toLowerCase();
    return !!domain && cfg.ssoDomains.includes(domain);
  }

  onSubmit(): void {
    if (this.profileForm.valid) {
      this.store.dispatch(
        updateUser({
          newUser: {
            ...this.profileForm.value,
            lastModifiedByDate: this.userProfile?.lastModifiedByDate,
          },
        })
      );
    }
  }
}
